import React, { createContext, useContext, useState } from 'react';
import {
  Modal,
  TouchableOpacity,
} from 'react-native';
import { useTheme } from '@/lib/styling';
import { Button } from './Button';
import { Box, Text } from '@/components/ui/primitives';
import type { Theme } from '@/theme';
import { StyleSheet } from 'react-native-unistyles';

const overlayColor = 'rgba(22, 36, 28, 0.72)';
const styles = StyleSheet.create(() => ({
  overlay: {
    backgroundColor: overlayColor,
  },
}));

interface AlertDialogContextType {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

const AlertDialogContext = createContext<AlertDialogContextType | undefined>(undefined);

const useAlertDialogContext = () => {
  const context = useContext(AlertDialogContext);
  if (!context) {
    throw new Error('AlertDialog components must be used within an AlertDialog provider');
  }
  return context;
};

interface AlertDialogProps {
  children: React.ReactNode;
  defaultOpen?: boolean;
}

interface AlertDialogTriggerProps {
  asChild?: boolean;
  children: React.ReactNode;
}

interface AlertDialogContentProps {
  children: React.ReactNode;
}

interface AlertDialogHeaderProps {
  children: React.ReactNode;
}

interface AlertDialogTitleProps {
  children: React.ReactNode;
}

interface AlertDialogDescriptionProps {
  children: React.ReactNode;
}

interface AlertDialogFooterProps {
  children: React.ReactNode;
}

interface AlertDialogActionProps {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: 'default' | 'destructive'; // Explicit variant
  className?: string; // Keep for compatibility but unused
}

interface AlertDialogCancelProps {
  children: React.ReactNode;
  onClick?: () => void;
}

export function AlertDialog({ children, defaultOpen = false }: AlertDialogProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <AlertDialogContext.Provider value={{ isOpen, setIsOpen }}>
      {children}
    </AlertDialogContext.Provider>
  );
}

export function AlertDialogTrigger({ children }: AlertDialogTriggerProps) {
  const { setIsOpen } = useAlertDialogContext();

  // Clone the child element and add onPress
  if (React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement<any>, {
      onPress: () => setIsOpen(true),
    });
  }

  return (
    <TouchableOpacity onPress={() => setIsOpen(true)}>
      {children}
    </TouchableOpacity>
  );
}

export function AlertDialogContent({ children }: AlertDialogContentProps) {
  const { isOpen, setIsOpen } = useAlertDialogContext();
  const theme = useTheme<Theme>();

  return (
    <Modal
      visible={isOpen}
      transparent
      animationType="fade"
      onRequestClose={() => setIsOpen(false)}
    >
      <Box
        flex={1}
        alignItems="center"
        justifyContent="center"
        padding="l"
        style={styles.overlay}
      >
        <Box
          minWidth={280}
          maxWidth="90%"
          borderRadius="none"
          borderWidth={5}
          borderColor="press"
          backgroundColor="surface"
          padding="l"
          shadowColor={theme.colors.press}
          shadowOffset={{ width: 12, height: 12 }}
          shadowOpacity={1}
          shadowRadius={0}
          elevation={8}
        >
          {children}
        </Box>
      </Box>
    </Modal>
  );
}

export function AlertDialogHeader({ children }: AlertDialogHeaderProps) {
  return (
    <Box marginBottom="m">
      {children}
    </Box>
  );
}

export function AlertDialogTitle({ children }: AlertDialogTitleProps) {
  return (
    <Text
      variant="title" // text-lg
      marginBottom="s" // mb-2
      textTransform="uppercase"
      color="text"
    >
      {children}
    </Text>
  );
}

export function AlertDialogDescription({ children }: AlertDialogDescriptionProps) {
  return (
    <Text
      variant="bodySmall" // text-sm
      color="pressMuted"
      lineHeight={20}
    >
      {children}
    </Text>
  );
}

export function AlertDialogFooter({ children }: AlertDialogFooterProps) {
  return (
    <Box
      marginTop="l" // mt-6
      width="100%"
      flexDirection="row"
      alignItems="center"
      justifyContent="space-between"
    >
      {React.Children.map(children, (child, index) => {
        if (!React.isValidElement(child)) return child;
        return (
          <Box
            flex={1}
            marginLeft={index > 0 ? 'm' : 'none'} // ml-4
          >
            {child}
          </Box>
        );
      })}
    </Box>
  );
}

export function AlertDialogCancel({ children, onClick }: AlertDialogCancelProps) {
  const { setIsOpen } = useAlertDialogContext();

  const handlePress = () => {
    if (onClick) {
      onClick();
    }
    setIsOpen(false);
  };

  return (
    <Button
      variant="outline"
      onPress={handlePress}
      paddingHorizontal="l" // px-5
      paddingVertical="s" // py-2
    >
      <Text fontWeight="600" color="text">
        {children}
      </Text>
    </Button>
  );
}

export function AlertDialogAction({ children, onClick, className, variant = 'default' }: AlertDialogActionProps) {
  const { setIsOpen } = useAlertDialogContext();

  const handlePress = () => {
    if (onClick) {
      onClick();
    }
    setIsOpen(false);
  };

  // Compatibility check: if className has 'red', force destructive
  const resolvedVariant = className?.includes('red') ? 'destructive' : variant;

  return (
    <Button
      onPress={handlePress}
      variant={resolvedVariant}
      paddingHorizontal="l" // px-6
      paddingVertical="s" // py-2
    >
      {/* Button handles text color based on variant automatically, but we can be explicit if needed */}
      {typeof children === 'string' ? children : (
        <Text
          fontWeight="600"
          color={resolvedVariant === 'destructive' ? 'destructiveForeground' : 'primaryForeground'}
        >
          {children}
        </Text>
      )}
    </Button>
  );
}
